import styled from '@emotion/styled'
import {
    createContext,
    useContext,
    useCallback,
    useState,
    forwardRef,
    useImperativeHandle,
} from 'react'
import { Slot } from '@radix-ui/react-slot'
import {
    PRIMARY_ICON_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
} from '../../context/CustomizationContextProvider'
import { createPortal } from 'react-dom'
import { CloseIcon } from '../icons/Close'
import { InspectOverlayHost } from './InspectOverlayHost'

// Utilities to capture a ref to PagesContainer element so it can be used to render a portal

const DialogTargetContext = createContext<{
    targetEl: HTMLDivElement | null
    refCallback?: (node: HTMLDivElement | null) => void
}>({ targetEl: null })

const PagesContainer = styled.div({
    borderRadius: 0,
    position: 'relative',
    minHeight: '100vh',
    width: '100%',
})

const DialogTargetElement = styled.div({
    position: 'fixed',
    inset: 0,
    flexDirection: 'column',
    pointerEvents: 'none',
})

const DialogTarget: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [targetEl, setTargetEl] = useState<HTMLDivElement | null>(null)
    const refCallback = useCallback((node: HTMLDivElement | null) => {
        setTargetEl(node)
    }, [])
    return (
        <DialogTargetContext.Provider value={{ targetEl, refCallback }}>
            <PagesContainer>{children}</PagesContainer>
        </DialogTargetContext.Provider>
    )
}

// Component to render the portal target element - must be placed inside container
const DialogPortalTarget: React.FC<{ zIndex?: number }> = ({
    zIndex = 9999,
}) => {
    const { refCallback } = useContext(DialogTargetContext)
    return <DialogTargetElement ref={refCallback} style={{ zIndex }} />
}

/**
 * Returns a getter that reports whether any dialog is currently portaled into
 * `DialogPortalTarget`. Live: reads the DOM at call time so a global keydown
 * handler on a lower overlay (e.g. the cart) can defer to whatever sits on
 * top instead of stealing the keystroke.
 */
export const useIsPortaledDialogOpen = (): (() => boolean) => {
    const { targetEl } = useContext(DialogTargetContext)
    return useCallback(
        () => !!targetEl && targetEl.childElementCount > 0,
        [targetEl],
    )
}

// Dialog UI Components

export interface DialogHandle {
    open: () => void
    close: () => void
}

const DialogContext = createContext<{
    open: boolean
    setOpen: (open: boolean) => void
}>({ open: false, setOpen: () => {} })

const Dialog = forwardRef<DialogHandle, { children: React.ReactNode }>(
    ({ children }, ref) => {
        const [open, setOpen] = useState(false)

        useImperativeHandle(
            ref,
            () => ({
                open: () => setOpen(true),
                close: () => setOpen(false),
            }),
            [],
        )

        return (
            <DialogContext.Provider value={{ open, setOpen }}>
                {children}
            </DialogContext.Provider>
        )
    },
)

Dialog.displayName = 'Dialog'

const DialogTrigger: React.FC<
    React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
> = ({ children, asChild = false, ...props }) => {
    const { setOpen } = useContext(DialogContext)
    const Comp = asChild ? Slot : 'button'

    return (
        <Comp
            onClick={() => {
                setOpen(true)
            }}
            {...props}
        >
            {children}
        </Comp>
    )
}

const DialogOverlayElement = styled(InspectOverlayHost)({
    position: 'absolute',
    inset: 0,
    pointerEvents: 'auto',
    zIndex: 1000,
    background: 'rgba(0, 0, 0, 0.5)',
})

const DialogOverlay: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
    children,
    ...props
}) => {
    const { targetEl } = useContext(DialogTargetContext)
    const { open, setOpen } = useContext(DialogContext)
    if (!targetEl || !open) return null
    return createPortal(
        <DialogOverlayElement
            onClick={(event) => {
                if (event.target === event.currentTarget) setOpen(false)
            }}
            {...props}
        >
            {children}
        </DialogOverlayElement>,
        targetEl,
    )
}

const DialogContentElement = styled.div({
    position: 'fixed',
    inset: '15px',
    background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
})

const DialogContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
    role = 'dialog',
    'aria-modal': ariaModal = 'true',
    ...props
}) => {
    return (
        <DialogContentElement role={role} aria-modal={ariaModal} {...props} />
    )
}

const CloseIconButton = styled.button({
    background: 'transparent',
    border: 'none',
})

const DialogClose: React.FC<
    React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
> = ({ children, asChild = false, ...props }) => {
    const { setOpen } = useContext(DialogContext)

    if (asChild) {
        return (
            <Slot
                onClick={() => setOpen(false)}
                aria-label="Close dialog"
                {...props}
            >
                {children}
            </Slot>
        )
    }

    return (
        <CloseIconButton
            onClick={() => setOpen(false)}
            aria-label="Close dialog"
            {...props}
        >
            <CloseIcon
                width={24}
                height={24}
                fill={`var(${PRIMARY_ICON_COLOR_VAR})`}
            />
        </CloseIconButton>
    )
}

export {
    Dialog,
    DialogTrigger,
    DialogOverlay,
    DialogContent,
    DialogTarget,
    DialogClose,
    DialogPortalTarget,
}
